import {
  forwardRef,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
} from 'react';
import Grid from 'tui-grid';
import 'tui-grid/dist/tui-grid.css';
import $ from 'jquery';
import PropTypes from 'prop-types';
import getGridColumns from './GridColumns';
import getGridOptions from './GridOptions';
import GbitTablePaging from '../tableComponents/GbitTablePaging';

import { dbPost } from '../../apis/commonApi';
import getGridHeader from './GridHeaders';

const GbitGrid = forwardRef(
  (
    {
      id,
      modalService,
      gridHeight = '100%',
      headerClick,
      cellClick,
      cellDblClick,
      caption,
      callback,
    },
    ref
  ) => {
    const gridContainer = useRef(null);

    const [grid, setGrid] = useState(null);
    const [columns, setColumns] = useState([]);
    const [header, setHeader] = useState(() => {
      return id !== null ? getGridHeader(id) : {};
    });
    const [datas, setDatas] = useState([]);
    const [pagingYn, setPagingYn] = useState(() => {
      return id !== null ? getGridOptions(id).pagingYn : true;
    });
    const [pagingSize, setPagingSize] = useState(() => {
      return id !== null ? getGridOptions(id).pageSize : 10;
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCnt, setTotalCnt] = useState(0);

    const [storedUrl, setStoredUrl] = useState(null);
    const [storedParam, setStoredParam] = useState(null);

    useImperativeHandle(ref, () => {
      return {
        async doQuery(url, param) {
          const isChangedParam =
            JSON.stringify(storedParam) !== JSON.stringify(param);
          setStoredUrl(url);
          setStoredParam(param);
          if (isChangedParam && currentPage !== 1) {
            setCurrentPage(1);
          } else {
            await doQueryFunc(url, param);
          }
        },
        getColumnList() {
          return $.extend(true, [], columns);
        },
        getDatas() {
          return grid.getData();
          // return $.extend(true, [], datas);
        },
        setDatas(tableDatas) {
          setDatas($.extend(true, [], tableDatas));
        },
        getRowObj(row) {
          return grid.getRow(row);
        },
        setRowObj(row, rowData) {
          grid.setRow(row, rowData);
        },
        getCellValue(row, col) {
          return grid.getValue(row, col);
        },
        setCellValue(row, column, value) {
          grid.setValue(row, column, value, false);
        },
        hideColumn(columnName) {
          grid.hideColumn(columnName);
        },
      };
    });

    const doQueryFunc = async (url, param) => {
      const tempParam = $.extend(true, {}, param);

      if (pagingYn) {
        tempParam.pageSize = pagingSize;
        tempParam.currentPage = currentPage;
      }

      let response = null;

      try {
        response = await dbPost(url, tempParam);

        if (response instanceof Error) {
          modalService.errorAlert(
            '데이터 처리중 에러가 발생하였습니다',
            () => {}
          );
        } else {
          if (pagingYn && response.length > 0) {
            setTotalCnt(response[0].totalCount);
          } else if (pagingYn && response.length === 0) {
            setTotalCnt(0);
          }
          setDatas(response);
          if (callback !== undefined && callback instanceof Function) {
            callback(response);
          }
        }
      } catch (error) {
        modalService.errorAlert(
          '데이터 처리중 에러가 발생하였습니다',
          () => {}
        );
      }
    };

    useEffect(() => {
      if (storedUrl !== null && storedParam !== null) {
        doQueryFunc(storedUrl, storedParam);
      }
    }, [currentPage]);

    useEffect(() => {
      if (grid !== null) {
        grid.setColumns(columns);
      }
    }, [grid && columns]);

    const reWriteDatas = () => {
      if (grid !== null) {
        grid.resetData(datas);
      }
    };

    useEffect(() => {
      reWriteDatas();
    }, [datas]);

    const init = async () => {
      Grid.setLanguage('ko');
      Grid.applyTheme('default');

      const createGrid = async () => {
        const newGrid = new Grid({
          el: gridContainer.current,
          columns: columns,
          header: header,
          rowHeight: 52,
          // pageOptions: {
          //   useClient: true,
          //   perPage: pagingSize,
          // },
        });

        newGrid.on('click', ev => {
          if (ev.targetType === 'cell') {
            if (cellClick !== undefined && cellClick instanceof Function) {
              cellClick(
                ev.rowKey,
                ev.columnName,
                ev.nativeEvent.target.tagName
              );
            }
          } else if (ev.targetType === 'header') {
            if (headerClick !== undefined && headerClick instanceof Function) {
              headerClick(ev.columnName);
            }
          }
        });

        newGrid.on('dblclick', ev => {
          if (ev.targetType === 'cell') {
            if (
              cellDblClick !== undefined &&
              cellDblClick instanceof Function
            ) {
              const rowKey = ev.rowKey;
              const rowData = newGrid.getRow(rowKey);
              cellDblClick(ev.rowKey, ev.columnName, rowData);
            }
          }
        });

        newGrid.on('afterChange', ev => {});

        setGrid(newGrid);
      };

      const setOptionSource = async () => {
        if (id !== null) {
          const optionTemp = getGridOptions(id);

          if (optionTemp.pagingYn) {
            setPagingYn(optionTemp.pagingYn);
            setPagingSize(optionTemp.pageSize);
          }
        }
      };

      const setColumnSource = async () => {
        if (id !== null) {
          setColumns(getGridColumns(id));
        }
      };

      await createGrid().then(async () => {
        await setColumnSource();
        await setOptionSource();
      });
    };

    useEffect(() => {
      init();
      return () => {
        if (grid) {
          grid.destroy();
        }
      };
    }, []);

    return (
      <div ref={ref}>
        <div
          ref={gridContainer}
          id={`${id}_gridArea`}
          style={{ width: '100%', height: gridHeight }}
          aria-label={caption}
        />
        {pagingYn && totalCnt > 0 && (
          <GbitTablePaging
            id={`${id}_pagingArea`}
            totalItems={totalCnt}
            itemsPerPage={pagingSize}
            currentPage={currentPage}
            onPageChange={page => {
              setCurrentPage(page);
            }}
          />
        )}
      </div>
    );
  }
);

export default GbitGrid;

GbitGrid.propTypes = {
  id: PropTypes.string.isRequired,
  cellClick: PropTypes.func,
};
